import { Card, CardContent } from '../ui/card';
import { Briefcase, CheckCircle, TrendingUp, Banknote, ChevronRight, FileText, CircleDollarSign, Target } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';

export function DashboardStatsCards({ stats, onValidatedClick, onCommissionClick, onInProgressClick, onProspectsClick, onCommissionsValideesClick, onCommissionsACherchererClick }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    // Commission visible par tous les rôles commerciaux (chacun voit ses propres commissions)
    // Admin voit tout, VP voit les siennes + équipe, Apporteur voit les siennes
    const canSeeCommissions = ['admin_full', 'admin', 'comptable', 'vp', 'apporteur'].includes(user?.role);
    
    // Handler pour naviguer vers les prospects
    const handleProspectsClick = () => {
        if (onProspectsClick) {
            onProspectsClick();
        } else {
            navigate('/prospects');
        }
    };
    
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Prospects totaux - NOUVEAU: cliquable vers la liste des prospects */}
            <Card 
                className="cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 border-l-slate-500 hover:scale-[1.02]"
                onClick={handleProspectsClick}
                data-testid="card-total-prospects"
            >
                <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500">Total dossiers</p>
                            <p className="text-3xl font-bold text-slate-900">{stats.totalProspects || 0}</p>
                            <p className="text-xs text-slate-400 mt-1">Tous confondus</p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-slate-600" />
                        </div>
                    </div>
                    <p className="text-xs text-slate-600 mt-2 flex items-center">
                        <ChevronRight className="w-4 h-4" />
                        Cliquer pour voir
                    </p>
                </CardContent>
            </Card>

            {/* Dossiers en cours - cliquable pour scroller vers la section */}
            <Card 
                className="cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 border-l-blue-500 hover:scale-[1.02]"
                onClick={onInProgressClick}
                data-testid="card-in-progress"
            >
                <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500">En cours</p>
                            <p className="text-3xl font-bold text-slate-900">{stats.inProgressCount}</p>
                            <p className="text-xs text-slate-400 mt-1">Non finalisés</p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <Briefcase className="w-6 h-6 text-blue-600" />
                        </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2 flex items-center">
                        <ChevronRight className="w-4 h-4" />
                        Cliquer pour voir
                    </p>
                </CardContent>
            </Card>

            {/* Dossiers valides */}
            <Card 
                className="cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 border-l-green-500 hover:scale-[1.02]"
                onClick={onValidatedClick}
                data-testid="card-validated"
            >
                <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500">Gagnés</p>
                            <p className="text-3xl font-bold text-slate-900">{stats.validatedCount}</p>
                            <p className="text-xs text-slate-400 mt-1">Étape Gagné</p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                    </div>
                    <p className="text-xs text-green-600 mt-2 flex items-center">
                        <ChevronRight className="w-4 h-4" />
                        Cliquer pour voir
                    </p>
                </CardContent>
            </Card>

            {/* Moyenne par dossier - Visible uniquement pour Admin+ */}
            {canSeeCommissions && (
                <Card 
                    className="cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 border-l-amber-500 hover:scale-[1.02]"
                    onClick={onCommissionClick}
                    data-testid="card-average"
                >
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Moyenne / dossier</p>
                                <p className="text-3xl font-bold text-slate-900">
                                    {stats.avgCommission.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                                </p>
                                <p className="text-xs text-slate-400 mt-1">Commission moyenne</p>
                            </div>
                            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-amber-600" />
                            </div>
                        </div>
                        <p className="text-xs text-amber-600 mt-2 flex items-center">
                            <ChevronRight className="w-4 h-4" />
                            Voir détails
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Total commissions - Visible uniquement pour Admin+ */}
            {canSeeCommissions && (
                <Card 
                    className="cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 border-l-purple-500 hover:scale-[1.02]"
                    onClick={onCommissionClick}
                    data-testid="card-commissions"
                >
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Total commissions</p>
                                <p className="text-3xl font-bold text-slate-900">
                                    {stats.totalCommission.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                                </p>
                                <p className="text-xs text-slate-400 mt-1">Toutes périodes</p>
                            </div>
                            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                                <Banknote className="w-6 h-6 text-purple-600" />
                            </div>
                        </div>
                        <p className="text-xs text-purple-600 mt-2 flex items-center">
                            <ChevronRight className="w-4 h-4" />
                            Cliquer pour details
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Commissions validées (gagnées) - Admin+ uniquement */}
            {canSeeCommissions && (
                <Card 
                    className="cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 border-l-emerald-500 hover:scale-[1.02]"
                    onClick={onCommissionsValideesClick}
                    data-testid="card-commissions-validees"
                >
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Commissions validées</p>
                                <p className="text-3xl font-bold text-emerald-600">
                                    {(stats.commissionsValidees || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                                </p>
                                <p className="text-xs text-slate-400 mt-1">{stats.prospectsGagnes?.length || 0} dossiers gagnés</p>
                            </div>
                            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                                <CircleDollarSign className="w-6 h-6 text-emerald-600" />
                            </div>
                        </div>
                        <p className="text-xs text-emerald-600 mt-2 flex items-center">
                            <ChevronRight className="w-4 h-4" />
                            Voir les dossiers
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Commissions à aller chercher - Admin+ uniquement */}
            {canSeeCommissions && (
                <Card 
                    className="cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 border-l-orange-500 hover:scale-[1.02]"
                    onClick={onCommissionsACherchererClick}
                    data-testid="card-commissions-a-chercher"
                >
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">À aller chercher</p>
                                <p className="text-3xl font-bold text-orange-600">
                                    {(stats.commissionsAAllerChercher || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                                </p>
                                <p className="text-xs text-slate-400 mt-1">{stats.prospectsAAllerChercher?.length || 0} dossiers en cours</p>
                            </div>
                            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                                <Target className="w-6 h-6 text-orange-600" />
                            </div>
                        </div>
                        <p className="text-xs text-orange-600 mt-2 flex items-center">
                            <ChevronRight className="w-4 h-4" />
                            Voir les dossiers
                        </p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
